import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { RUN_DETAIL_TABS } from "../types";

const GO_ROUTES: Record<string, string> = {
  h: "/",
  n: "/",
  r: "/runs",
  c: "/coverage",
  p: "/policies",
  t: "/triage",
  e: "/exploration",
};

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return el.isContentEditable;
}

/**
 * The global keyboard map.
 *
 * `⌘K` (or `/`) opens the command palette; `g` then a key jumps between the
 * destinations; `1–4` switch run-detail tabs; `?` shows the legend. Keys are
 * ignored while typing, so the palette's own input never re-triggers them.
 */
export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const [helpOpen, setHelpOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const pendingG = useRef(false);
  const gTimer = useRef<number | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // The palette is reachable with a modifier, so it is checked before the
      // modifier guard below.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((open) => !open);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;

      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        e.preventDefault();
        setHelpOpen((open) => !open);
        return;
      }
      if (e.key === "/") {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }
      if (e.key === "Escape") {
        setHelpOpen(false);
        setPaletteOpen(false);
        return;
      }

      if (pendingG.current) {
        pendingG.current = false;
        if (gTimer.current != null) window.clearTimeout(gTimer.current);
        const dest = GO_ROUTES[e.key.toLowerCase()];
        if (dest) {
          e.preventDefault();
          navigate(dest);
        }
        return;
      }

      if (e.key === "g") {
        e.preventDefault();
        pendingG.current = true;
        gTimer.current = window.setTimeout(() => {
          pendingG.current = false;
        }, 1000);
        return;
      }

      if (/^[1-4]$/.test(e.key) && /^\/runs\/[^/]+$/.test(location.pathname)) {
        e.preventDefault();
        const tab = RUN_DETAIL_TABS[Number(e.key) - 1];
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("tab", tab);
            return next;
          },
          { replace: true },
        );
      }
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, location.pathname, setSearchParams]);

  return { helpOpen, setHelpOpen, paletteOpen, setPaletteOpen };
}

export const SHORTCUT_LEGEND = [
  { keys: "⌘K / /", action: "Command palette" },
  { keys: "?", action: "Keyboard shortcuts" },
  { keys: "g h", action: "Now" },
  { keys: "g r", action: "Runs" },
  { keys: "g c", action: "Coverage" },
  { keys: "g p", action: "Policies" },
  { keys: "g t", action: "Triage" },
  { keys: "g e", action: "Exploration" },
  { keys: "1–4", action: "Run detail tabs" },
] as const;
